import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { UrlDev } from '../url';
import '../entrydoor.css';
import { useNavigate } from 'react-router-dom';
import { alertC } from './../components/dialogService';
import "./Login.css";

// ✅ Ensure Axios sends cookies with requests
axios.defaults.withCredentials = true;

// Cloudflare Turnstile site key (public). Captcha is skipped on localhost or
// any IP address (dev/staging); enabled only on real domain names.
const TURNSTILE_SITE_KEY = '0x4AAAAAADKywn8cWyhKGzaW';
const captchaEnabled = !['localhost', '127.0.0.1'].includes(window.location.hostname)
  && !/^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dealer, setDealer] = useState('');
  const [dealerEnabled, setDealerEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/assets/company_logo.png');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = Cookies.get('access_token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }

    // Fetch company logo for login page (without authentication)
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${UrlDev}/company/`);
        if (response.data && response.data.logo_path) {
          if (response.data.logo_path.startsWith('/images/')) {
            setLogoUrl(UrlDev + response.data.logo_path);
          } else {
            setLogoUrl(response.data.logo_path);
          }
        }
      } catch (error) {
        console.log('Using default logo');
      }
    };

    // Fetch dealer feature flag
    const fetchDealerEnabled = async () => {
      try {
        const response = await axios.get(`${UrlDev}/settings/dealer_enabled`);
        setDealerEnabled(response.data.dealer_enabled === true);
      } catch (error) {
        console.log('Could not fetch dealer setting, defaulting to disabled');
      }
    };

    fetchLogo();
    fetchDealerEnabled();
  }, []);

  // Render Turnstile widget once the global API is loaded
  useEffect(() => {
    if (!captchaEnabled) return;
    if (!turnstileRef.current) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile) {
        setTimeout(tryRender, 200);
        return;
      }
      if (widgetIdRef.current) return; // already rendered
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(null),
        'error-callback': () => setCaptchaToken(null),
      });
    };
    tryRender();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (captchaEnabled && !captchaToken) {
      await alertC('Please complete the captcha.');
      setIsLoading(false);
      return;
    }

    try {
      const res = await axios.post(`${UrlDev}/auth/login`, {
        username,
        password,
        dealer: dealerEnabled ? dealer : undefined,
        captcha_token: captchaToken,
      }, {
        withCredentials: true,
      });

      const { access_token, csrf_token, user_role, user_role_label, user_role_pages, user_id, email, crm_user_id, crm_role, last_login, dealer_id, dealer_code } = res.data;
      if (!access_token || !csrf_token || !user_role || !user_id || !email) {
        throw new Error('Invalid login response');
      }

      localStorage.setItem("csrf_token", csrf_token);
      localStorage.setItem("user", username);
      // Store role pages for dynamic route access control
      if (user_role_pages) {
        localStorage.setItem("user_role_pages", JSON.stringify(user_role_pages));
      }
      if (user_role_label) {
        localStorage.setItem("user_role_label", user_role_label);
      }
      if (last_login) {
        localStorage.setItem("last_login", last_login);
      } else {
        localStorage.removeItem("last_login");
      }
      if (crm_user_id) {
        Cookies.set('crm_user_id', crm_user_id);
      }
      Cookies.set('crm_role', crm_role || '', { expires: 1 });
      // [VP-IDLE-LOGOUT 2026-05-27] cookie expires in 1 day to match the backend JWT (24h);
      // logout is now driven by inactivity via utils/useIdleLogout.js (default 15 min).
      Cookies.set('access_token', access_token, { expires: 1, secure: true, sameSite: 'Strict' });
      if (dealer_id) Cookies.set('dealer_id', String(dealer_id), { expires: 1 });
      if (dealer_code) Cookies.set('dealer_code', dealer_code, { expires: 1 });
      const loginSuccess = onLogin({ role: user_role, id: user_id, email });
      if (!loginSuccess) throw new Error('Login failed');

      navigate('/quotes');
    } catch (err) {
      console.error('Login error:', err);
      // On any failure, reset the captcha so the user can try again with a fresh token
      if (captchaEnabled && widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setCaptchaToken(null);
      }
      await alertC(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <div className="company-logo">
          <img src={logoUrl} alt="Company Logo" />
        </div>
        <h2>Login</h2>
        {dealerEnabled && (
          <input
            type="text"
            value={dealer}
            onChange={(e) => setDealer(e.target.value)}
            placeholder="Dealer"
            required
            className="login-input"
          />
        )}
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          required
          className="login-input"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          required
          className="login-input"
        />

        {captchaEnabled && (
          <div ref={turnstileRef} style={{ marginTop: '1rem', marginBottom: '1rem' }} />
        )}

        <button type="submit" disabled={isLoading} className="login-button">
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    </div>
  );
}
